import { CoralIndexedDBUtils } from "@framework/coral/coral-indexeddb";

/**
 * Centralized comment count store that manages comment counts for count key
 * with timestamp-based priority.
 *
 * This store provides:
 * - In-memory storage of comment counts with timestamps
 * - Subscription system for reactive updates
 * - Timestamp-based priority (newer data overwrites older)
 * - Automatic persistence to IndexedDB
 *
 * Read counts are handled separately as they don't need reactive subscriptions.
 */

export type CommentCountUpdate = {
  countKey: string;
  count: number;
  timestamp: number;
};

export type CommentCountData = {
  count: number;
  timestamp: number;
};

/**
 * Centralized comment count store that manages comment counts for count key
 * with timestamp-based priority.
 *
 * Priority System:
 * - Newer timestamps always take precedence over older ones
 * - This prevents race conditions between different data sources
 * - Timestamps are set by the caller to reflect when the data was actually generated
 */
export class CommentCountStore {
  private counts = new Map<string, CommentCountData>();
  private subscriptions = new Map<string, Set<(count: number) => void>>();

  /**
   * Get the current comment count for a coral ID
   * Returns null if no data, number if loaded (including 0)
   * This null pattern distinguishes between "not loaded" and "actually zero comments"
   */
  getCommentCount(coralId: string): number | null {
    const data = this.counts.get(coralId);
    return data ? data.count : null;
  }

  /**
   * Update comment count with timestamp-based priority
   * Only updates if the new data is newer than existing data
   */
  async updateCommentCount(update: CommentCountUpdate): Promise<void> {
    const { countKey, count, timestamp } = update;
    const existingData = this.counts.get(countKey);

    // Check if this update is newer than existing data
    if (existingData && existingData.timestamp >= timestamp) {
      // Existing data is newer or same age, skip this update
      return;
    }

    const newData: CommentCountData = {
      count,
      timestamp,
    };

    this.counts.set(countKey, newData);
    this.notifySubscribers(countKey, count);

    // Automatically save to IndexedDB
    await this.saveToIndexedDB([update]);
  }

  /**
   * Get comment count (alias for getCommentCount for backward compatibility)
   */
  getCount(coralId: string): number | null {
    return this.getCommentCount(coralId);
  }

  /**
   * Update multiple comment counts
   */
  async updateCounts(updates: CommentCountUpdate[]): Promise<void> {
    for (const update of updates) {
      await this.updateCommentCount(update);
    }
  }

  /**
   * Subscribe to comment count changes for a specific coral ID
   * Returns unsubscribe function
   */
  subscribe(coralId: string, callback: (count: number) => void): () => void {
    if (!this.subscriptions.has(coralId)) {
      this.subscriptions.set(coralId, new Set());
    }
    this.subscriptions.get(coralId)!.add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.subscriptions.get(coralId);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.subscriptions.delete(coralId);
        }
      }
    };
  }

  /**
   * Notify all subscribers of a comment count change
   */
  private notifySubscribers(coralId: string, count: number): void {
    const callbacks = this.subscriptions.get(coralId);
    if (callbacks) {
      callbacks.forEach((callback) => callback(count));
    }
  }

  /**
   * Clear all data from the store
   */
  clear(): void {
    this.counts.clear();
    this.subscriptions.clear();
  }

  /**
   * Get all stored coral IDs
   */
  getAllCoralIds(): string[] {
    return Array.from(this.counts.keys());
  }

  /**
   * Save updates to IndexedDB
   * This is called automatically when data is updated
   */
  private async saveToIndexedDB(updates: CommentCountUpdate[]): Promise<void> {
    // Fire and forget - don't block the UI
    const coralDb = await CoralIndexedDBUtils.init();
    await coralDb.saveCommentCounts(updates);
  }
}

// Export singleton instance
export const commentCountStore = new CommentCountStore();
